/**
 * Centralized Constants & Types for Buyer App
 */

// Auto-detect API URL based on window.location
// This allows mobile devices to connect using the computer's IP
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:3000`;
  }
  
  return 'http://localhost:3000';
};

const getWsUrl = () => {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const hostname = window.location.hostname;
    return `ws://${hostname}:3000`;
  }
  
  return 'ws://localhost:3000';
};

export const API_URL = getApiUrl();
export const WS_URL = getWsUrl();

// Performance budgets
export const PERFORMANCE = {
  TTI_TARGET_MS: 2500,
  INTERACTION_TARGET_MS: 250,
  BUNDLE_SIZE_KB: 500,
} as const;

// API endpoints
export const ENDPOINTS = {
  AUTH: {
    SEND_OTP: '/api/auth/send-otp',
    VERIFY_OTP: '/api/auth/verify-otp',
    REFRESH: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
  },
  ZONES: {
    LIST: '/api/zones',
    DETAILS: '/api/zones/:id',
  },
  VENDORS: {
    SEARCH: '/api/vendors/search',
    DETAILS: '/api/vendors/:id',
    MENU: '/api/vendors/:id/menu',
  },
  ORDERS: {
    LIST: '/api/buyer/orders',
    CREATE: '/api/buyer/orders',
    DETAILS: '/api/buyer/orders/:id',
    CANCEL: '/api/buyer/orders/:id/cancel',
  },
  PROMOS: {
    LIST: '/api/promos',
    APPLY: '/api/promos/apply',
    VALIDATE: '/api/promos/validate',
  },
  CHAT: {
    CREATE_THREAD: '/api/chat/:orderId/thread',
    GET_MESSAGES: '/api/chat/:threadId',
    SEND_MESSAGE: '/api/chat/:threadId/message',
    TYPING: '/api/chat/:threadId/typing',
  },
  RATINGS: {
    CREATE: '/api/ratings',
    GET_ORDER: '/api/ratings/orders/:orderId',
    GET_AGGREGATED: '/api/ratings/aggregated/:type/:id',
  },
  MEDIA: {
    UPLOAD: '/api/media/upload',
    DELETE: '/api/media/:id',
    GET: '/api/media/:id',
  },
} as const;

// WebSocket channels
export const WS_CHANNELS = {
  ORDERS_BUYER: 'order-updates', // Backend channel: EventChannel.ORDER_UPDATES
  CHAT: 'chat-messages',
  ALERTS: 'system-alerts', // Backend channel: EventChannel.SYSTEM_ALERTS
} as const;

// Order statuses
export type OrderStatus =
  | 'placed'
  | 'accepted'
  | 'preparing'
  | 'ready_for_pickup'
  | 'picked_up'
  | 'in_transit'
  | 'delivered'
  | 'cancelled';

// User role
export type UserRole = 'buyer' | 'vendor' | 'rider' | 'admin';

// Payment method
export type PaymentMethod = 'cod' | 'gcash' | 'paymaya' | 'card';

// Zone interface
export interface Zone {
  id: string;
  name: string;
  description?: string;
  geo?: any; // GeoJSON geometry
  min_lat: string;
  max_lat: string;
  min_lng: string;
  max_lng: string;
  storm_mode: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Vendor interface
export interface Vendor {
  id: string;
  name: string;
  description?: string;
  category?: string;
  zone_id: string;
  status: 'online' | 'offline' | 'busy';
  rating?: number;
  delivery_eta_min: number;
  delivery_eta_max: number;
  delivery_fee: number;
  min_order: number;
  is_open: boolean;
}

// Menu item interface
export interface MenuItem {
  id: string;
  vendor_id: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  price_today: number;
  image_url?: string; // Legacy
  primary_image_id?: string; // Sprint 4I
  gallery_image_ids?: string[]; // Sprint 4I
  primaryImage?: ProductImage; // Sprint 4I: Resolved URLs
  galleryImages?: ProductImage[]; // Sprint 4I: Resolved URLs
  is_active: boolean;
  stock_status: 'in_stock' | 'low_stock' | 'out_of_stock';
}

// Cart item interface
export interface CartItem {
  menuItem: MenuItem;
  quantity: number;
  options?: any;
  subtotal: number;
}

// Order interface
export interface Order {
  id: string;
  buyer_id: string;
  vendor_id: string;
  vendor_name: string;
  items: OrderItem[];
  subtotal: number;
  delivery_fee: number;
  promo_discount: number;
  total: number;
  status: OrderStatus;
  payment_method: PaymentMethod;
  delivery_address: string;
  delivery_lat?: number;
  delivery_lng?: number;
  promo_code?: string;
  created_at: string;
  updated_at: string;
  estimated_delivery_time?: string;
  rider_id?: string;
  rider_name?: string;
}

export interface OrderItem {
  id: string;
  product_id: string;
  name: string;
  quantity: number;
  price: number;
  subtotal: number;
}

// Sprint 4I: Product Image
export interface ProductImage {
  id: string;
  smUrl: string;
  mdUrl: string;
  lgUrl: string;
  width?: number;
  height?: number;
}

// Sprint 4I: Rating & Review
export interface Rating {
  id: string;
  order_id: string;
  buyer_user_id: string;
  target_type: 'vendor' | 'rider';
  target_id: string;
  rating_value: number; // 1-5
  comment?: string;
  review_image_id?: string;
  reviewImage?: ProductImage;
  created_at: string;
  updated_at: string;
}

export interface AggregatedRating {
  target_type: 'vendor' | 'rider';
  target_id: string;
  avg_rating: number;
  ratings_count: number;
  reliability_score: number;
  last_rating_at?: string;
}

export type ReliabilityBadge = 'bronze' | 'silver' | 'gold' | 'platinum';

// Buyer profile interface
export interface BuyerProfile {
  id: string;
  phone: string;
  name?: string;
  email?: string;
  default_address?: string;
  default_lat?: number;
  default_lng?: number;
}

// Address interface
export interface Address {
  id: string;
  buyer_id: string;
  label: string;
  street: string;
  barangay: string;
  city: string;
  latitude: number;
  longitude: number;
  is_default: boolean;
}

// Promo interface
export interface Promo {
  id: string;
  code: string;
  kind: 'percentage' | 'fixed_amount' | 'free_delivery';
  value: number;
  min_order?: number;
  max_discount?: number;
  caps_json?: {
    user_per_day?: number;
    device_per_day?: number;
    payment_method_per_day?: number;
    global_per_day?: number;
  };
  starts_at: string;
  ends_at: string;
  is_active: boolean;
}

// Promo validation result
export interface PromoValidation {
  valid: boolean;
  discount: number;
  message?: string;
  reason?: 'invalid' | 'expired' | 'min_order' | 'user_cap' | 'device_cap' | 'payment_cap' | 'global_cap';
  audit_id?: string;
}

// Chat message interface
export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderRole: UserRole;
  message: string;
  timestamp: string;
  read: boolean;
}

// Stock status
export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';